import * as THREE from "three";

import { GameScreen } from "../ui/screens/game_screen/game_screen.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { setBlockMassMultiplier } from "../physics/physics_masses.js";
import { SimulationContext } from "../simulation/simulation_context.js";
import { yieldToBrowser } from "../simulation/yield_to_browser.js";
import { TestLevelBridge } from "./test_level_bridge.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";
import { getMaxCampaignLevel } from "../level_config.js";
import { ScreenManager, Screens } from "../screen_manager/screen_manager.js";

const SETTLE_SECONDS = 2.5;
const FIXED_DT = 1 / 60;

const MOVE_TOLERANCE = 0.08;

export interface StabilityResult {
  level: number;
  stable: boolean;

  spawned: number;
  survived: number;

  maxDrift: number;
  reason: 'stable' | 'pieces-fell' | 'drifted' | 'empty';
}

async function checkOne( level_number: number ): Promise<StabilityResult> {
  const screen = new GameScreen();

  SimulationContext.enabled = true;
  // Stability verdicts describe base masses (×1), never a segment multiplier.
  setBlockMassMultiplier( 1 );
  screen.enableSimulation( () => {}, false );

  try {
    await screen.init( { level: level_number } );

    screen.update( 0 );

    const before = screen.getAliveTargets().map( t => t.position.clone() );

    if ( before.length === 0 ) {
      return { level: level_number, stable: false, spawned: 0, survived: 0, maxDrift: 0, reason: 'empty' };
    }

    const steps = Math.round( SETTLE_SECONDS / FIXED_DT );
    for ( let i = 0; i < steps; i++ ) {
      PhysicsWorld.update( FIXED_DT );

      screen.update( FIXED_DT );
    }

    const after = screen.getAliveTargets();

    if ( after.length < before.length ) {
      return {
        level: level_number,
        stable: false,
        spawned: before.length,
        survived: after.length,
        maxDrift: 0,
        reason: 'pieces-fell',
      };
    }

    let max_drift = 0;
    for ( let i = 0; i < before.length; i++ ) {
      max_drift = Math.max( max_drift, before[i].distanceTo( after[i].position ) );
    }

    return {
      level: level_number,
      stable: max_drift <= MOVE_TOLERANCE,
      spawned: before.length,
      survived: after.length,
      maxDrift: max_drift,
      reason: max_drift <= MOVE_TOLERANCE ? 'stable' : 'drifted',
    };
  }
  finally {
    screen.destroy();
    SimulationContext.enabled = false;
  }
}

export interface AwaitedBatch {
  count: number;
  /**
   * True only when the editor never answered at all. An empty batch is NOT a
   * timeout — it is the editor saying "nothing left for you", which is a normal
   * way for a window to finish when the others drained the queue first.
   */
  timedOut: boolean;
}

export function awaitBatch( timeout_ms = 20_000 ): Promise<AwaitedBatch> {
  if ( TestLevelBridge.getBatchLevels().length > 0 )
    return Promise.resolve( { count: TestLevelBridge.getBatchLevels().length, timedOut: false } );

  return new Promise( resolve => {
    const timer = setTimeout( () => {
      window.removeEventListener( "message", onMessage );
      console.warn( "[Stability] редактор не надіслав пачку" );
      resolve( { count: 0, timedOut: true } );
    }, timeout_ms );

    const onMessage = ( event: MessageEvent ) => {
      const data = event.data as { type?: string; levels?: unknown; runs?: unknown } | null;
      if ( !data || data.type !== "brickbreaker-stability-batch" )
        return;

      clearTimeout( timer );
      window.removeEventListener( "message", onMessage );

      // A chunk may carry its own run count — see TestLevelBridge.setSimRuns.
      if ( typeof data.runs === "number" )
        TestLevelBridge.setSimRuns( data.runs );

      resolve( { count: TestLevelBridge.setBatch( data.levels ), timedOut: false } );
    };

    window.addEventListener( "message", onMessage );

    try {
      window.opener?.postMessage( { type: "brickbreaker-stability-ready" }, "*" );
    }
    catch ( e ) {
      console.warn( "[Stability] не вдалося повідомити редактор про готовність", e );
    }
  } );
}

export async function runStabilityBatch(): Promise<void> {
  const { count } = await awaitBatch();
  if ( count === 0 )
    return;

  const levels = TestLevelBridge.getBatchLevels();
  if ( levels.length === 0 )
    return;

  const results: StabilityResult[] = [];

  for ( const level_number of levels ) {
    TestLevelBridge.showBatchProgress( results.length + 1, levels.length );

    try {
      const result = await checkOne( level_number );
      results.push( result );
      console.log(
        `[Stability] #${result.level}: ${result.stable ? 'стійкий' : result.reason}` +
        ` · фігур ${result.spawned}->${result.survived} · зсув ${result.maxDrift.toFixed(4)}`
      );
    }
    catch ( e ) {
      console.warn( `[Stability] level ${level_number} failed to run`, e );
      results.push( {
        level: level_number,
        stable: false,
        spawned: 0,
        survived: 0,
        maxDrift: 0,
        reason: 'empty',
      } );
    }

    await yieldToBrowser();
  }

  const stable = results.filter( r => r.stable ).length;

  const drifts = results.filter( r => r.reason !== 'pieces-fell' ).map( r => r.maxDrift ).sort( ( a, b ) => a - b );
  const at = ( q: number ) => drifts.length ? drifts[Math.min( drifts.length - 1, Math.floor( drifts.length * q ) )] : 0;

  console.log(
    `[Stability] ${stable}/${results.length} стійких · поріг ${MOVE_TOLERANCE}` +
    ( drifts.length ? ` · зсув p50 ${at( 0.5 ).toFixed(4)} p90 ${at( 0.9 ).toFixed(4)} max ${at( 1 ).toFixed(4)}` : '' ),
    results
  );

  TestLevelBridge.showBatchSummary( stable, results.length );

  try {
    window.opener?.postMessage( { type: 'brickbreaker-stability', results }, '*' );
  }
  catch ( e ) {
    console.warn( '[Stability] could not report back to the editor', e );
  }
}

export interface StabilityPoolReport {
  generated_at: string;
  source: 'release' | 'bundled' | 'batch';
  release_uid: string | null;
  levels_checked: number;
  stable: number;
  unstable: StabilityResult[];
  drift_p50: number;
  drift_p90: number;
  drift_max: number;
  results: StabilityResult[];
}

/**
 * Settles every level the game currently plays — the active server release
 * when one is loaded, the bundled table otherwise — and reports which ones
 * do not stand still. Console-driven: `checkStabilityAll()` from devtools,
 * optionally `checkStabilityAll(from, to)` for a slice. Downloads the report
 * as JSON so it can be attached to a ticket or diffed between releases.
 */
export async function checkStabilityPool( from = 1, to = 0, download = true ): Promise<StabilityPoolReport> {
  // A bridge batch is its own pool: its levels outrank the release, and it may
  // be longer or shorter than the campaign, so it sets the range itself.
  const batch = TestLevelBridge.getBatchLevels();
  const max = batch.length > 0 ? Math.max( ...batch ) : getMaxCampaignLevel();
  const last = to > 0 ? Math.min( to, max ) : max;
  const first = Math.max( 1, from );
  const source: StabilityPoolReport['source'] = batch.length > 0 ? 'batch' : LevelPackService.isActive() ? 'release' : 'bundled';
  const release_uid = batch.length > 0 ? null : LevelPackService.getReleaseUid();

  // A live GameScreen shares the one Rapier world with the headless checks: its
  // bodies keep stepping under the level being settled and turn the first few
  // verdicts into noise. Leave for the menu first, so the run starts on an
  // empty world.
  if ( ScreenManager.getCurrentScreen() === Screens.GAME ) {
    console.log( "[Stability] на екрані жива гра — виходжу в меню, щоб світ був порожній" );
    await ScreenManager.showScreen( Screens.MENU );
    await yieldToBrowser();
  }

  console.log( `[Stability] пул ${source}${release_uid ? ` ${release_uid}` : ''} · рівні ${first}–${last}` );

  const results: StabilityResult[] = [];
  const started = performance.now();
  // The Rapier world is a process-wide singleton. A level whose teardown leaks
  // a body would poison every level after it — so the body count is checked
  // between levels and any growth is reported loudly.
  const bodies_at_start = PhysicsWorld.counts().bodies;

  for ( let level = first; level <= last; level++ ) {
    let result: StabilityResult;
    // In batch mode a level the bridge cannot serve would be answered by the
    // release or the bundled table instead — a verdict about the wrong level.
    if ( batch.length > 0 && !batch.includes( level ) ) {
      console.warn( `[Stability] #${level}: у пачці немає такого рівня — пропущено, щоб не перевіряти чужий` );
      results.push( { level, stable: false, spawned: 0, survived: 0, maxDrift: 0, reason: 'empty' } );
      continue;
    }
    try {
      result = await checkOne( level );
    }
    catch ( e ) {
      console.warn( `[Stability] level ${level} failed to run`, e );
      result = { level, stable: false, spawned: 0, survived: 0, maxDrift: 0, reason: 'empty' };
    }
    results.push( result );

    const bodies_now = PhysicsWorld.counts().bodies;
    if ( bodies_now !== bodies_at_start )
      console.warn( `[Stability] #${level}: у світі лишилось ${bodies_now - bodies_at_start} зайвих тіл після destroy — наступні вердикти під підозрою` );

    if ( !result.stable )
      console.warn( `[Stability] #${result.level}: ${result.reason} · фігур ${result.spawned}->${result.survived} · зсув ${result.maxDrift.toFixed( 4 )}` );

    if ( results.length % 25 === 0 || level === last ) {
      const elapsed = ( performance.now() - started ) / 1000;
      const per_level = elapsed / results.length;
      const remaining = ( last - level ) * per_level;
      console.log( `[Stability] ${results.length}/${last - first + 1} · нестійких ${results.filter( r => !r.stable ).length} · ~${Math.round( remaining )}с залишилось` );
    }

    await yieldToBrowser();
  }

  const unstable = results.filter( r => !r.stable );
  const drifts = results.filter( r => r.reason !== 'pieces-fell' ).map( r => r.maxDrift ).sort( ( a, b ) => a - b );
  const at = ( q: number ) => drifts.length ? drifts[Math.min( drifts.length - 1, Math.floor( drifts.length * q ) )] : 0;

  const report: StabilityPoolReport = {
    generated_at: new Date().toISOString(),
    source,
    release_uid,
    levels_checked: results.length,
    stable: results.length - unstable.length,
    unstable,
    drift_p50: at( 0.5 ),
    drift_p90: at( 0.9 ),
    drift_max: at( 1 ),
    results,
  };

  console.log(
    `[Stability] ГОТОВО: ${report.stable}/${report.levels_checked} стійких · поріг ${MOVE_TOLERANCE}` +
    ` · зсув p50 ${report.drift_p50.toFixed( 4 )} p90 ${report.drift_p90.toFixed( 4 )} max ${report.drift_max.toFixed( 4 )}`
  );
  if ( unstable.length )
    console.table( unstable.map( r => ( { level: r.level, reason: r.reason, spawned: r.spawned, survived: r.survived, drift: Number( r.maxDrift.toFixed( 4 ) ) } ) ) );

  if ( download )
    downloadReport( report );

  return report;
}

function downloadReport( report: StabilityPoolReport ): void {
  try {
    const blob = new Blob( [ JSON.stringify( report, null, 2 ) ], { type: "application/json" } );
    const url = URL.createObjectURL( blob );
    const link = document.createElement( "a" );
    link.href = url;
    link.download = `stability-${report.source}-${report.release_uid ?? 'bundled'}-${report.levels_checked}lvl.json`;
    link.click();
    URL.revokeObjectURL( url );
  }
  catch ( e ) {
    console.warn( "[Stability] не вдалося зберегти звіт", e );
  }
}

/**
 * Settles a list of raw level configs (the game-format payload — the same shape
 * the release blob and the editor push carry) without them being part of any
 * release: they are handed to TestLevelBridge, which outranks both the server
 * pack and the bundled table, so a candidate file can be checked BEFORE it is
 * imported anywhere. Results are keyed by position in the array (level = i+1).
 */
export async function checkStabilityBatch( raw_configs: unknown[], download = false ): Promise<StabilityPoolReport> {
  // The bridge keys levels by their `level` field, and the pool walk below asks
  // for 1..N by position. A payload that keeps its original numbering (90, 106,
  // …) would make every position miss the bridge and silently fall through to
  // whatever release the game has loaded — so it is renumbered by position here.
  const renumbered = raw_configs.map( ( config, index ) =>
    config && typeof config === "object" ? { ...( config as Record<string, unknown> ), level: index + 1 } : config
  );
  const count = TestLevelBridge.setBatch( renumbered );
  if ( count === 0 ) {
    console.warn( "[Stability] batch is empty or unparsable" );
    return { generated_at: new Date().toISOString(), source: 'batch', release_uid: null, levels_checked: 0, stable: 0, unstable: [], drift_p50: 0, drift_p90: 0, drift_max: 0, results: [] };
  }
  try {
    return await checkStabilityPool( 1, count, download );
  }
  finally {
    TestLevelBridge.setBatch( [] );
  }
}

export function exposeStabilityCheck(): void {
  ( window as any ).checkStability = async ( level: number ) => checkOne( level );
  ( window as any ).checkStabilityAll = ( from = 1, to = 0, download = true ) => checkStabilityPool( from, to, download );
  ( window as any ).checkStabilityBatch = ( configs: unknown[], download = false ) => checkStabilityBatch( configs, download );
}
